import React from "react"
import PropTypes from "prop-types"
import Slider from "react-slick"
import { useTheme } from "@mui/material/styles"
import Card from "@mui/material/Card"
import Box from "@mui/material/Box"
import useMediaQuery from "@mui/material/useMediaQuery"
import CardContent from "@mui/material/CardContent"
import Typography from "@mui/material/Typography"
import Avatar from "@mui/material/Avatar"
import { colors } from "@mui/material"
import Container from "common/Container"
import HomeIcon from "@mui/icons-material/Home"
import BusinessIcon from "@mui/icons-material/Business"
import TrendingUpIcon from "@mui/icons-material/TrendingUp"
import AssessmentIcon from "@mui/icons-material/Assessment"
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff"
import StorefrontIcon from "@mui/icons-material/Storefront"

const Services = ({ themeMode = "light" }) => {
    const theme = useTheme()
    const isXs = useMediaQuery(theme.breakpoints.up("xs"), {
        defaultMatches: true,
    })
    const isSm = useMediaQuery(theme.breakpoints.up("sm"), {
        defaultMatches: true,
    })
    const isMd = useMediaQuery(theme.breakpoints.up("md"), {
        defaultMatches: true,
    })
    const isLg = useMediaQuery(theme.breakpoints.up("lg"), {
        defaultMatches: true,
    })

    let slidesToShow = 1
    if (isXs) {
        slidesToShow = 1
    }
    if (isSm) {
        slidesToShow = 2
    }
    if (isMd) {
        slidesToShow = 3
    }
    if (isLg) {
        slidesToShow = 4
    }

    const sliderOpts = {
        dots: true,
        infinite: true,
        speed: 500,
        slidesToShow,
        slidesToScroll: 1,
        arrows: false,
    }

    return (
        <Box>
            <Container paddingY={"0 !important"}>
                <Box marginBottom={4}>
                    <Typography
                        sx={{
                            textTransform: "uppercase",
                            fontWeight: "medium",
                        }}
                        gutterBottom
                        color={"secondary"}
                    >
                        Our Services
                    </Typography>
                    <Box
                        component={Typography}
                        fontWeight={700}
                        variant={"h3"}
                        color={"textPrimary"}
                        gutterBottom
                    >
                        Comprehensive Real Estate Solutions
                    </Box>
                    <Typography
                        variant={"h6"}
                        component={"p"}
                        color={"textSecondary"}
                    >
                        We&apos;re here to guide you through every step of your
                        real estate journey
                    </Typography>
                </Box>
            </Container>
            <Box>
                <Box
                    sx={{
                        "& .slick-slide > div": {
                            height: "100%",
                        },
                        "& .slick-track": {
                            display: "flex",
                            alignItems: "stretch",
                        },
                        "& .slick-slide": {
                            height: "auto",
                            "& > div": {
                                height: "100%",
                            },
                        },
                    }}
                >
                    <Slider {...sliderOpts}>
                    {[
                        {
                            title: "Residential Sales",
                            subtitle:
                                "Expert guidance in buying or selling your home, ensuring you get the best value in the market.",
                            icon: <HomeIcon sx={{ fontSize: 48 }} />,
                            color: colors.purple,
                        },
                        {
                            title: "Property Management",
                            subtitle:
                                "Comprehensive management services for property owners, maximizing investment returns.",
                            icon: <BusinessIcon sx={{ fontSize: 48 }} />,
                            color: colors.green,
                        },
                        {
                            title: "Investment Consulting",
                            subtitle:
                                "Strategic advice on real estate investments, helping you build a profitable property portfolio.",
                            icon: <TrendingUpIcon sx={{ fontSize: 48 }} />,
                            color: colors.amber,
                        },
                        {
                            title: "Market Analysis",
                            subtitle:
                                "In-depth analysis of local real estate trends to inform your property decisions.",
                            icon: <AssessmentIcon sx={{ fontSize: 48 }} />,
                            color: colors.pink,
                        },
                        {
                            title: "Relocation Services",
                            subtitle:
                                "Comprehensive support for individuals and families moving to or from San Diego.",
                            icon: <FlightTakeoffIcon sx={{ fontSize: 48 }} />,
                            color: colors.blue,
                        },
                        {
                            title: "Commercial Real Estate",
                            subtitle:
                                "Expert services for buying, selling, or leasing commercial properties in prime locations.",
                            icon: <StorefrontIcon sx={{ fontSize: 48 }} />,
                            color: colors.teal,
                        },
                    ].map((item, i) => (
                        <Box padding={{ xs: 1, md: 2, lg: 3 }} key={i}>
                            <Box
                                component={Card}
                                boxShadow={{ xs: 1, sm: 3 }}
                                borderRadius={5}
                                sx={{
                                    height: "100%",
                                    minHeight: { xs: 280, sm: 300, md: 320 },
                                    display: "flex",
                                    flexDirection: "column",
                                }}
                            >
                                <Box
                                    component={CardContent}
                                    display={"flex"}
                                    flexDirection={"column"}
                                    alignItems={"center"}
                                    padding={{ xs: 2, sm: 4, md: 6 }}
                                    sx={{
                                        flexGrow: 1,
                                        "&:last-child": {
                                            paddingBottom: {
                                                xs: 2,
                                                sm: 4,
                                                md: 6,
                                            },
                                        },
                                    }}
                                >
                                    <Box
                                        component={Avatar}
                                        variant="rounded"
                                        width={84}
                                        height={84}
                                        marginBottom={2}
                                        bgcolor={
                                            themeMode === "light"
                                                ? item.color[50]
                                                : item.color[200]
                                        }
                                        borderRadius={5}
                                    >
                                        <Box
                                            color={
                                                item.color[
                                                    themeMode === "light"
                                                        ? 500
                                                        : 700
                                                ]
                                            }
                                        >
                                            {item.icon}
                                        </Box>
                                    </Box>
                                    <Box
                                        component={Typography}
                                        variant={"h6"}
                                        gutterBottom
                                        fontWeight={500}
                                        align={"center"}
                                    >
                                        {item.title}
                                    </Box>
                                    <Typography
                                        align={"center"}
                                        color="textSecondary"
                                        sx={{
                                            flexGrow: 1,
                                            display: "flex",
                                            alignItems: "center",
                                        }}
                                    >
                                        {item.subtitle}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    ))}
                    </Slider>
                </Box>
            </Box>
        </Box>
    )
}

Services.propTypes = {
    themeMode: PropTypes.string.isRequired,
}

export default Services
